import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Unlock, Plus, Trash2, Pencil, Eye, EyeOff, Save, X, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import IrisChat from './IrisChat';
import LostLoveChat from './LostLoveChat';
import ClosureRoom from './ClosureRoom';
import CompanionChat from './CompanionChat';
import PasswordVault from './PasswordVault';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EditorModal({ initial, pin, onSaved, onClose }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [content, setContent] = useState(initial?.content || '');
  const [saving, setSaving] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const save = async () => {
    if (!title.trim()) {
      toast.error('Title required');
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.patch(
          `${API}/secret-notes/${initial.id}`,
          { pin, title: title.trim(), content },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API}/secret-notes`,
          { pin, title: title.trim(), content },
          { withCredentials: true }
        );
      }
      toast.success('Saved securely 🔐');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="secret-editor-modal"
    >
      <div
        className="neo-card bg-white dark:bg-[#252545] max-w-lg w-full max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Lock className="w-5 h-5" /> {initial?.id ? 'Edit secret note' : 'New secret note'}
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (e.g. WiFi password)"
          maxLength={120}
          className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54] mb-3"
          data-testid="secret-title-input"
        />
        <div className="relative mb-3">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={8}
            maxLength={5000}
            placeholder="Secret content…"
            className={`w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54] resize-none pr-10 ${showContent ? '' : 'font-mono tracking-widest'}`}
            style={showContent ? {} : { WebkitTextSecurity: 'disc' }}
            data-testid="secret-content-input"
          />
          <button
            type="button"
            onClick={() => setShowContent((s) => !s)}
            className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 border-2 border-black"
            title={showContent ? 'Hide' : 'Show'}
            data-testid="secret-toggle-visibility"
          >
            {showContent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="secret-save-btn"
        >
          <Save className="w-4 h-4" /> {saving ? 'Encrypting…' : 'Save securely'}
        </button>
      </div>
    </div>
  );
}

function NoteCard({ note, onEdit, onDelete }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="neo-card p-4 bg-white dark:bg-[#252545]" data-testid={`secret-note-${note.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-bold font-['Fredoka'] truncate flex items-center gap-1">
            <Lock className="w-3.5 h-3.5 text-pink-500" /> {note.title}
          </p>
          <p className="text-[10px] opacity-60 mt-0.5">
            Updated {note.updated_at ? new Date(note.updated_at).toLocaleDateString() : ''}
          </p>
        </div>
        <button onClick={() => onEdit(note)} className="neo-btn p-1.5 bg-white" title="Edit" data-testid={`secret-edit-${note.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(note)} className="neo-btn p-1.5 bg-[#FF6B6B] text-white" title="Delete" data-testid={`secret-delete-${note.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="rounded-xl border-2 border-dashed border-black/30 bg-[#FFFAF0] dark:bg-[#1f1f3a] p-3 relative">
        <button
          onClick={() => setRevealed((r) => !r)}
          className="absolute top-2 right-2 p-1 rounded-lg bg-white/90 border-2 border-black"
          title={revealed ? 'Hide' : 'Reveal'}
          data-testid={`secret-reveal-${note.id}`}
        >
          {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        </button>
        <pre
          className="text-xs whitespace-pre-wrap break-words font-['Nunito'] pr-6 min-h-[2em]"
          style={revealed ? {} : { filter: 'blur(6px)', userSelect: 'none' }}
        >
          {note.content || <span className="opacity-50">(empty)</span>}
        </pre>
      </div>
    </div>
  );
}

export default function SecretNotes() {
  const [status, setStatus] = useState(null); // { has_pin }
  const [pin, setPin] = useState(''); // in-memory only
  const [pinInput, setPinInput] = useState('');
  const [confirmPinInput, setConfirmPinInput] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showChangePin, setShowChangePin] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [subTab, setSubTab] = useState('notes'); // 'notes' | 'iris'

  useEffect(() => {
    axios
      .get(`${API}/secret-notes/status`, { withCredentials: true })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ has_pin: false }))
      .finally(() => setLoading(false));
  }, []);

  const fetchNotes = useCallback(async (currentPin) => {
    try {
      const { data } = await axios.post(
        `${API}/secret-notes/list`,
        { pin: currentPin },
        { withCredentials: true }
      );
      setNotes(data.items || []);
    } catch {
      // ignore
    }
  }, []);

  const setupPin = async () => {
    const p = pinInput.trim();
    if (p.length < 4) { toast.error('PIN must be at least 4 characters'); return; }
    if (p !== confirmPinInput.trim()) { toast.error('PINs don\'t match'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/secret-notes/setup`, { pin: p }, { withCredentials: true });
      setStatus({ has_pin: true });
      setPin(p);
      setUnlocked(true);
      await fetchNotes(p);
      toast.success('PIN set! 🔐');
      setPinInput(''); setConfirmPinInput('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    const p = pinInput.trim();
    if (!p) return;
    setBusy(true);
    try {
      await axios.post(`${API}/secret-notes/unlock`, { pin: p }, { withCredentials: true });
      setPin(p);
      setUnlocked(true);
      await fetchNotes(p);
      setPinInput('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wrong PIN');
    } finally {
      setBusy(false);
    }
  };

  const lock = () => {
    setPin('');
    setUnlocked(false);
    setNotes([]);
    toast.info('Locked 🔒');
  };

  const deleteNote = async (note) => {
    if (!window.confirm(`Delete "${note.title}"?`)) return;
    try {
      await axios.post(`${API}/secret-notes/${note.id}/delete`, { pin }, { withCredentials: true });
      toast.success('Deleted');
      await fetchNotes(pin);
    } catch {
      toast.error('Delete failed');
    }
  };

  const changePin = async () => {
    const np = newPin.trim();
    if (np.length < 4) { toast.error('New PIN must be at least 4 characters'); return; }
    setBusy(true);
    try {
      await axios.post(
        `${API}/secret-notes/change-pin`,
        { old_pin: pin, new_pin: np },
        { withCredentials: true }
      );
      setPin(np);
      setNewPin('');
      setShowChangePin(false);
      toast.success('PIN changed & all notes re-encrypted');
      await fetchNotes(np);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Change PIN failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Loading…</div>;

  // Setup flow (no PIN yet)
  if (!status.has_pin) {
    return (
      <div className="neo-card p-6 max-w-md mx-auto bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid="secret-setup">
        <div className="text-center mb-4">
          <div className="w-16 h-16 mx-auto rounded-full border-4 border-black flex items-center justify-center bg-[#FFC8DD] text-3xl">
            🔐
          </div>
          <h2 className="mt-3 text-2xl font-bold font-['Fredoka']">Set your PIN</h2>
          <p className="text-xs opacity-75 mt-1">
            This PIN encrypts every secret note. If you forget it, your notes are gone forever (by design).
          </p>
        </div>
        <input
          type="password"
          inputMode="numeric"
          value={pinInput}
          onChange={(e) => setPinInput(e.target.value)}
          placeholder="New PIN (4-20 chars)"
          className="w-full px-3 py-2 rounded-xl border-2 border-black mb-2"
          data-testid="secret-pin-setup-input"
        />
        <input
          type="password"
          inputMode="numeric"
          value={confirmPinInput}
          onChange={(e) => setConfirmPinInput(e.target.value)}
          placeholder="Confirm PIN"
          className="w-full px-3 py-2 rounded-xl border-2 border-black mb-3"
          data-testid="secret-pin-confirm-input"
        />
        <button
          onClick={setupPin}
          disabled={busy}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold disabled:opacity-50"
          data-testid="secret-setup-btn"
        >
          {busy ? 'Setting up…' : 'Create PIN & unlock'}
        </button>
      </div>
    );
  }

  // Locked
  if (!unlocked) {
    return (
      <div className="neo-card p-6 max-w-md mx-auto bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid="secret-locked">
        <div className="text-center mb-4">
          <div className="w-16 h-16 mx-auto rounded-full border-4 border-black flex items-center justify-center bg-[#CDB4DB] text-3xl">
            🔒
          </div>
          <h2 className="mt-3 text-2xl font-bold font-['Fredoka']">Secret Notes</h2>
          <p className="text-xs opacity-75 mt-1">Enter your PIN to unlock</p>
        </div>
        <input
          type="password"
          inputMode="numeric"
          value={pinInput}
          onChange={(e) => setPinInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && unlock()}
          placeholder="PIN"
          className="w-full px-3 py-2 rounded-xl border-2 border-black mb-3"
          data-testid="secret-pin-unlock-input"
          autoFocus
        />
        <button
          onClick={unlock}
          disabled={busy}
          className="neo-btn w-full py-3 bg-[#A2D2FF] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="secret-unlock-btn"
        >
          <Unlock className="w-4 h-4" /> {busy ? 'Unlocking…' : 'Unlock'}
        </button>
      </div>
    );
  }

  // Unlocked
  return (
    <div className="space-y-4" data-testid="secret-notes-unlocked">
      {/* Sub-tab switch: Notes vs Iris (privat chat) */}
      <div className="flex gap-2 bg-white/60 backdrop-blur rounded-2xl p-1 w-fit mx-auto sm:mx-0 border border-rose-100">
        <button
          onClick={() => setSubTab('notes')}
          data-testid="secret-subtab-notes"
          className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition ${
            subTab === 'notes' ? 'bg-[#CDB4DB] text-white shadow' : 'text-gray-600 hover:bg-white'
          }`}
        >
          🔒 Anteckningar
        </button>
        <button
          onClick={() => setSubTab('iris')}
          data-testid="secret-subtab-iris"
          className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition ${
            subTab === 'iris' ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow' : 'text-gray-600 hover:bg-white'
          }`}
        >
          💋 Iris
        </button>
        <button
          onClick={() => setSubTab('lostlove')}
          data-testid="secret-subtab-lostlove"
          className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition ${
            subTab === 'lostlove' ? 'bg-gradient-to-r from-rose-400 to-pink-400 text-white shadow' : 'text-gray-600 hover:bg-white'
          }`}
        >
          💌 Thomas
        </button>
        <button
          onClick={() => setSubTab('closure')}
          data-testid="secret-subtab-closure"
          className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition ${
            subTab === 'closure' ? 'bg-gradient-to-r from-purple-400 to-rose-400 text-white shadow' : 'text-gray-600 hover:bg-white'
          }`}
        >
          🪞 Upprättelse
        </button>
        <button
          onClick={() => setSubTab('mom')}
          data-testid="secret-subtab-mom"
          className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition ${
            subTab === 'mom' ? 'bg-gradient-to-r from-rose-300 to-amber-300 text-white shadow' : 'text-gray-600 hover:bg-white'
          }`}
        >
          🌸 Mamma
        </button>
        <button
          onClick={() => setSubTab('bestie')}
          data-testid="secret-subtab-bestie"
          className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition ${
            subTab === 'bestie' ? 'bg-gradient-to-r from-amber-300 to-sky-400 text-white shadow' : 'text-gray-600 hover:bg-white'
          }`}
        >
          🌟 Kompis
        </button>
        <button
          onClick={() => setSubTab('vault')}
          data-testid="secret-subtab-vault"
          className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition ${
            subTab === 'vault' ? 'bg-gradient-to-r from-emerald-400 to-teal-500 text-white shadow' : 'text-gray-600 hover:bg-white'
          }`}
        >
          🔐 Lösenord
        </button>
      </div>

      {subTab === 'iris' && <IrisChat />}
      {subTab === 'lostlove' && <LostLoveChat pin={pin} />}
      {subTab === 'closure' && <ClosureRoom pin={pin} />}
      {subTab === 'mom' && <CompanionChat pin={pin} roomType="mom" />}
      {subTab === 'vault' && <PasswordVault pin={pin} />}
      {subTab === 'bestie' && <CompanionChat pin={pin} roomType="bestie" />}

      {subTab === 'notes' && (
        <>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Unlock className="w-5 h-5" /> Secret Notes
          </h2>
          <p className="text-xs opacity-75">End-to-end encrypted with your PIN 🔐</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowChangePin((v) => !v)}
            className="neo-btn px-3 py-2 bg-[#FFE8A3] text-xs font-bold flex items-center gap-1"
            data-testid="secret-change-pin-btn"
          >
            <KeyRound className="w-3.5 h-3.5" /> Change PIN
          </button>
          <button
            onClick={lock}
            className="neo-btn px-3 py-2 bg-[#CDB4DB] text-xs font-bold flex items-center gap-1"
            data-testid="secret-lock-btn"
          >
            <Lock className="w-3.5 h-3.5" /> Lock
          </button>
          <button
            onClick={() => setCreating(true)}
            className="neo-btn px-3 py-2 bg-[#FFC8DD] text-xs font-bold flex items-center gap-1"
            data-testid="secret-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New
          </button>
        </div>
      </div>

      {showChangePin && (
        <div className="neo-card p-4 bg-[#FDF0D5] dark:bg-[#2a2a44] flex gap-2 items-end flex-wrap">
          <label className="flex-1 min-w-[200px]">
            <span className="text-xs font-bold block mb-1">New PIN</span>
            <input
              type="password"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              placeholder="New PIN (4-20)"
              className="w-full px-3 py-2 rounded-xl border-2 border-black"
              data-testid="secret-new-pin-input"
            />
          </label>
          <button
            onClick={changePin}
            disabled={busy || !newPin.trim()}
            className="neo-btn px-4 py-2 bg-[#B9FBC0] font-bold disabled:opacity-50"
            data-testid="secret-new-pin-save"
          >
            {busy ? 'Re-encrypting…' : 'Save new PIN'}
          </button>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white">
          <div className="text-4xl mb-2">🤫</div>
          <p className="font-bold font-['Fredoka']">No secret notes yet</p>
          <p className="text-xs opacity-75 mt-1">Tap New to create your first encrypted note.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {notes.map((n) => (
            <NoteCard key={n.id} note={n} onEdit={setEditing} onDelete={deleteNote} />
          ))}
        </div>
      )}

      {creating && (
        <EditorModal
          initial={null}
          pin={pin}
          onSaved={() => fetchNotes(pin)}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <EditorModal
          initial={editing}
          pin={pin}
          onSaved={() => fetchNotes(pin)}
          onClose={() => setEditing(null)}
        />
      )}
        </>
      )}
    </div>
  );
}
